import React, { useMemo } from 'react'
import { makeStyles, useTheme, Button, useTranslation, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'

import type { PresentationPage, PresentationSection } from 'paintscout'

import {
  getQuoteV1Presentation,
  filterActivePresentationPages,
  filterActivePresentationSections
} from '@paintscout/util/builder'

import find from 'lodash/find'

import classnames from 'classnames'
import { useQuote } from '../../../context'
import { usePresentationNav } from '../PresentationNavContext'

const useStyles = makeStyles((theme: Theme) => {
  return {
    textRoot: {
      '&::-moz-box-shadow': {
        // boxShadow: 'inset  0 -2px 3px -2px grey'
      },
      '&::-webkit-box-shadow': {
        // webkitBoxShadow: 'inset  0 -2px 3px -2px grey'
      },
      // boxShadow: 'inset  0 -2px 3px -2px grey',
      '&::-webkit-scrollbar': {
        width: '0.4em'
      },
      '&::-webkit-scrollbar-track': {
        // boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        // webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,.1)',
        outline: 'none'
      },
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '72vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      '& button': {
        marginTop: theme.spacing(1)
      }
    },
    buttonWrapper: {
      marginBottom: theme.spacing(1),
      '&:first-child button': {
        marginTop: 0
      },

      '&:last-child': {
        marginBottom: 0
      }
    },
    button: {
      whiteSpace: 'normal',
      textAlign: 'left',
      justifyContent: 'flex-start',
      height: 'auto',
      marginLeft: '10px',

      padding: `${theme.typography.pxToRem(5)} 0`,
      paddingTop: 0,
      borderRadius: 0,
      borderBottom: `2px solid transparent`,
      '&:hover': {
        color: theme.palette.primary.main,
        background: 'transparent'
      }
    },
    highlight: {
      // color: theme.palette.common.black
    },
    textDiv: {
      overflow: 'hidden'
      // width: '170px'
    },
    selectedText: {
      fontWeight: `${theme.typography.fontWeightBold} !important`,
      color: `${theme.paintscout?.palette?.brand?.Neutral?.P40 || theme.palette.common.black} !important`,
      '&, & *, & h4': {
        fontWeight: `${theme.typography.fontWeightBold} !important`,
        color: `${theme.paintscout?.palette?.brand?.Neutral?.P40 || theme.palette.common.black} !important`
      }
    },
    unselectedText: {
      fontWeight: `${theme.typography.fontWeightMedium} !important`,
      color: `${theme.paintscout?.palette?.brand?.Neutral?.P60 || theme.palette.text.secondary} !important`,
      '&, & *, & h4': {
        fontWeight: `${theme.typography.fontWeightMedium} !important`,
        color: `${theme.paintscout?.palette?.brand?.Neutral?.P60 || theme.palette.text.secondary} !important`
      }
    }
  }
})

export interface PresentationNavTextProps {}

const PresentationNavText = ({ ...props }: PresentationNavTextProps) => {
  const classes = useStyles(props)
  const theme = useTheme()
  const { options } = useClientOptions()
  const { quote } = useQuote()
  const { __ } = useTranslation()
  const { onPageClick, onSectionClick, isSinglePage, currentPage, currentSection, view } = usePresentationNav()

  const presentation = getQuoteV1Presentation({
    quote,
    options,
    isWorkOrder: view === 'work-order'
  })
  let activeItems = []
  let highlight = null
  if (isSinglePage) {
    activeItems = presentation.pages[0].sections.filter(filterActivePresentationSections)
    highlight = activeItems.reduce((acc: string) => {
      const estimateSection = find(activeItems, { type: 'quote' })
      return acc || (estimateSection ? estimateSection.key : null)
    }, null)
  } else {
    activeItems = presentation.pages.filter(filterActivePresentationPages)
    highlight = activeItems.reduce((acc: string, page: PresentationPage) => {
      const estimateSection = find(page.sections, { type: 'quote' })
      return acc || (estimateSection ? page.key : null)
    }, null)
  }

  const translatedTitles = useMemo(
    () => Object.fromEntries(activeItems.map((item: PresentationSection) => [item.key, ellipsize(__(item.title))])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeItems, __]
  )

  return (
    <div className={classes.textRoot}>
      {activeItems.map((item: PresentationSection, index) => {
        // Use the same isCurrentItem function for consistency
        // Convert undefined to null for consistent comparison
        // In single-page mode, currentPage is actually set to the section key via onScroll
        // So we need to use currentPage as fallback when currentSection is undefined
        const pageKey = currentPage ?? null
        const sectionKey = currentSection ?? (isSinglePage ? currentPage : null) ?? null
        const isPageSelected = !isSinglePage && isCurrentItem(item, index, pageKey)
        const isSectionSelected = isSinglePage && isCurrentItem(item, index, sectionKey)
        const isSelected = isPageSelected || isSectionSelected

        return (
          <div className={classes.buttonWrapper} key={item.key}>
            <Button
              disableRipple={true}
              classes={{
                root: classnames({
                  [classes.button]: true,
                  [classes.highlight]: highlight === item.key
                })
              }}
              variant={'text'}
              onClick={(ev) => {
                ev.preventDefault()
                if (isSinglePage && onSectionClick) {
                  onSectionClick(ev, item.key)
                } else if (onPageClick) {
                  onPageClick(ev, item.key)
                }
              }}
            >
              <div className={classes.textDiv}>
                <Typography
                  noWrap
                  style={{
                    whiteSpace: 'normal'
                  }}
                  variant={'h4'}
                  weight={
                    isSelected
                      ? (theme.typography.fontWeightBold as number)
                      : (theme.typography.fontWeightMedium as number)
                  }
                  className={classnames({
                    [classes.selectedText]: isSelected,
                    [classes.unselectedText]: !isSelected
                  })}
                >
                  {translatedTitles[item.key]}
                </Typography>
              </div>
            </Button>
          </div>
        )
      })}
      {/* {getPartnerPresentation(partnerContent, presentation)} */}
    </div>
  )

  function ellipsize(input: string) {
    if (!input) {
      return ''
    }
    const words = input.split(' ')
    words.forEach((word, index) => {
      if (word.length < 15) {
        words[index] = word
      } else {
        words[index] = `${word.slice(0, 12)}...`
      }
    })
    return words.join(' ')
  }

  function isCurrentItem(item: PresentationSection, index: number, currentKey: string | null | undefined) {
    // If currentKey is null/undefined and this is the first item, it's selected
    // Otherwise, check if the item key matches the current key
    return (currentKey == null && index === 0) || item.key === currentKey
  }
}

export default PresentationNavText
